import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Checkbox, DialogContent, Text } from "@octopusdeploy/design-system-components";
import { fontWeight, space, fontSize } from "@octopusdeploy/design-system-tokens";
import { logger } from "@octopusdeploy/logging";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import { Permission, OctopusError } from "@octopusdeploy/octopus-server-client";
import type { UserResource, CreateSupportRequestCommand, SupportFileMetadata } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useState, useCallback, useEffect, useMemo } from "react";
import { repository, session } from "~/clientInstance";
import ErrorPanel from "~/components/ErrorPanel/ErrorPanel";
import { UserMultiSelect } from "~/components/MultiSelect/UserMultiSelect";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { FileUploadDragDrop } from "~/components/form/index";
import Dialog from "../Dialog/Dialog";
import { SupportTicketSuccessDialog } from "./SupportTicketSuccessDialog";
const ALLOWED_FILE_EXTENSIONS = ["txt", "log", "json", "xml", "config", "yml", "yaml", "properties", "har", "zip", "tar", "gz", "png", "jpg", "jpeg", "pdf", "csv", "mp4", "webm", "mov"];
export interface SupportTicketDialogProps {
    open: boolean;
    onClose: () => void;
}
export interface SupportTicketData {
    name: string;
    email: string;
    includeOtherUsers?: string[];
    subject: string;
    description: string;
    attachments: File[];
    includeSystemReport: boolean;
}
const styles = {
    formRow: css({
        display: "flex",
        gap: space[16],
    }),
    formField: css({
        flex: 1,
    }),
    section: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    dialogContent: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    uploadSectionTitle: css({
        fontWeight: fontWeight[600],
        textAlign: "left",
    }),
    technicalSectionTitle: css({
        fontWeight: fontWeight[600],
    }),
    fileTypesHelp: css({
        fontSize: fontSize.small,
        marginTop: space[4],
    }),
};
function isUserResourceArray(users: unknown): users is UserResource[] {
    return Array.isArray(users);
}
export function SupportTicketDialog({ open, onClose }: SupportTicketDialogProps) {
    const [formData, setFormData] = useState<SupportTicketData>({
        name: "",
        email: "",
        includeOtherUsers: [],
        subject: "",
        description: "",
        attachments: [],
        includeSystemReport: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [systemReportWarning, setSystemReportWarning] = useState<string | null>(null);
    const { result: usersResult = [] } = useInlineStatusQuery(async (repository): Promise<UserResource[]> => (isAllowed({ permission: Permission.UserView }) ? repository.Users.all() : Promise.resolve([])), [], "Load users for support ticket");
    const users = useMemo(() => {
        return usersResult && isUserResourceArray(usersResult) ? usersResult : [];
    }, [usersResult]);
    const uploadSupportFiles = useCallback(async (files: File[]): Promise<SupportFileMetadata[]> => {
        if (!files || files.length === 0) {
            return [];
        }
        const responses = await Promise.all(files.map((file) => repository.Support.uploadSupportFile(file)));
        return responses.map((response) => response.SupportFileMetadata);
    }, []);
    const submitSupportRequest = useCallback(async () => {
        if (submitting)
            return;
        setSubmitting(true);
        try {
            // Convert user IDs to email addresses
            const ccRecipients: string[] = formData.includeOtherUsers?.map((userId) => users.find((u) => u.Id === userId)?.EmailAddress).filter((email): email is string => Boolean(email)) || [];
            // Upload files first if any exist
            const uploadedFileMetadata = await uploadSupportFiles(formData.attachments);
            // Create support request with uploaded file metadata
            const command: CreateSupportRequestCommand = {
                Subject: formData.subject,
                Description: formData.description,
                UserName: formData.name,
                UserEmail: formData.email,
                IncludeSystemReport: formData.includeSystemReport,
                CcRecipients: ccRecipients,
                UploadedFileMetadata: uploadedFileMetadata.length > 0 ? uploadedFileMetadata : undefined,
            };
            const response = await repository.Support.createSupportRequest(command);
            // Capture any system report warning
            setSystemReportWarning(response.SystemReportWarning || null);
            onClose();
            setShowSuccessDialog(true);
        }
        catch (error) {
            logger.error("Failed to submit support request", error);
            console.log(error);
            setError(error);
        }
        finally {
            setSubmitting(false);
        }
    }, [formData, users, onClose, submitting, uploadSupportFiles]);
    // Reset form and populate user data when dialog opens
    useEffect(() => {
        if (open) {
            // Reset success dialog state and clear any errors
            setShowSuccessDialog(false);
            setError(null);
            setSystemReportWarning(null);
            // Populate name and email from current user
            if (session.currentUser) {
                setFormData((prev) => ({
                    ...prev,
                    name: session.currentUser?.DisplayName || "",
                    email: session.currentUser?.EmailAddress || "",
                }));
            }
        }
        else {
            // Reset form when dialog closes
            setFormData({
                name: "",
                email: "",
                includeOtherUsers: [],
                subject: "",
                description: "",
                attachments: [],
                includeSystemReport: true,
            });
        }
    }, [open]);
    const handleInputChange = useCallback((field: keyof SupportTicketData) => (value: string | boolean | string[]) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);
    const handleSubmit = useCallback(async () => {
        await submitSupportRequest();
    }, [submitSupportRequest]);
    const handleFilesChanged = useCallback((files: File[]) => {
        // Validate file types
        const invalidFiles = files.filter((file) => {
            const extension = file.name.split(".").pop()?.toLowerCase();
            return !extension || !ALLOWED_FILE_EXTENSIONS.includes(extension);
        });
        if (invalidFiles.length > 0) {
            const invalidNames = invalidFiles.map((f) => f.name).join(", ");
            const fileWord = invalidFiles.length === 1 ? "file" : "files";
            setError(new Error(`Invalid ${fileWord}: ${invalidNames}. Please select a file in one of the supported formats.`));
            return;
        }
        // Clear any previous errors and set valid files
        setError(null);
        setFormData((prev) => ({
            ...prev,
            attachments: files,
        }));
    }, []);
    const handleSuccessDialogClose = useCallback(() => {
        setShowSuccessDialog(false);
    }, []);
    const actions = (<>
            <ActionButton label="Close" onClick={onClose} disabled={submitting}/>
            <ActionButton type={ActionButtonType.Save} label={submitting ? "Submitting..." : "Submit Ticket"} onClick={handleSubmit} disabled={submitting || !formData.subject || !formData.description || !formData.name || !formData.email}/>
        </>);
    return (<>
            <Dialog open={open} title="Lodge support ticket" dialogWidth="600px" actions={actions}>
                <DialogContent className={styles.dialogContent}>
                    {error && (<ErrorPanel message={OctopusError.isOctopusError(error) ? error.ErrorMessage : error.message || "Failed to submit support ticket. Please try again or contact support directly."} errors={OctopusError.isOctopusError(error) ? error.Errors : undefined} scrollToPanel={false}/>)}

                    <div>
                        Contact support using the form below. We'll get back to you shortly. You can also visit our{" "}
                        <a href="https://octopus.com/support" target="_blank" rel="noopener noreferrer">
                            support page
                        </a>
                        .
                    </div>

                    {/* Name and Email Row */}
                    <div className={styles.formRow}>
                        <div className={styles.formField}>
                            <Text label="Name" value={formData.name} onChange={handleInputChange("name")}/>
                        </div>
                        <div className={styles.formField}>
                            <Text label="Email" value={formData.email} onChange={handleInputChange("email")} type="email"/>
                        </div>
                    </div>

                    {/* Include other users */}
                    <UserMultiSelect label="Include other users" value={formData.includeOtherUsers || []} onChange={handleInputChange("includeOtherUsers")} items={users.filter((user) => user.EmailAddress && user.EmailAddress !== session.currentUser?.EmailAddress)}/>

                    {/* Subject */}
                    <Text label="Subject" value={formData.subject} onChange={handleInputChange("subject")}/>

                    {/* Description */}
                    <Text label="Describe the problem" value={formData.description} onChange={handleInputChange("description")} multiline minRows={4} maxRows={8}/>

                    {/* File upload section */}
                    <div className={styles.section}>
                        <div className={styles.uploadSectionTitle}>Upload supporting documents (optional)</div>
                        <div className={styles.fileTypesHelp}>Supported formats: {ALLOWED_FILE_EXTENSIONS.join(", ")}</div>
                        <FileUploadDragDrop label="Drag and drop files here or click to browse (max 1 GB per file)" onFilesChanged={handleFilesChanged} filesLimit={10}/>
                    </div>

                    {/* Technical issues section */}
                    <div className={styles.section}>
                        <div className={styles.technicalSectionTitle}>For technical issues</div>
                        <Checkbox label="Send System Diagnostics Report" value={formData.includeSystemReport} onChange={handleInputChange("includeSystemReport")}/>
                        <div>Please note that the support team may need temporary read-only access to your instance to understand and diagnose the problem.</div>
                    </div>
                </DialogContent>
            </Dialog>

            <SupportTicketSuccessDialog open={showSuccessDialog} onClose={handleSuccessDialogClose} systemReportWarning={systemReportWarning}/>
        </>);
}
